import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  User,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  FileText,
  AlertCircle,
  Heart,
  Users,
  Link2,
  CheckCircle,
  XCircle,
  Trash2,
  Search,
  X,
  Download,
  Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_REFERIDO = [
  { value: 'novio', label: 'Novio', comision: 5, descripcion: '5% de comisión' },
  { value: 'invitado', label: 'Invitado', comision: 3, descripcion: '3% de comisión' },
  { value: 'mascota', label: 'Mascota', comision: 3, descripcion: '3% de comisión' },
  { value: 'paje', label: 'Paje', comision: 3, descripcion: '3% de comisión' },
  { value: 'corte_completa', label: 'Corte Completa', comision: 5, descripcion: '5% de comisión' },
  { value: 'otro', label: 'Otro', comision: 2, descripcion: '2% de comisión' }
];

const ESTADOS_REFERIDO = [
  { value: 'registrado', label: 'Registrado', color: 'zinc' },
  { value: 'pendiente_contacto', label: 'Pendiente de contacto', color: 'amber' },
  { value: 'contactado', label: 'Contactado', color: 'blue' },
  { value: 'cotizado', label: 'Cotizado', color: 'purple' },
  { value: 'pendiente_ficha', label: 'Pendiente ficha de pedido', color: 'orange' },
  { value: 'ficha_creada', label: 'Ficha creada', color: 'cyan' },
  { value: 'venta_realizada', label: 'Venta realizada', color: 'green' },
  { value: 'no_fue_venta', label: 'No fue venta', color: 'red' },
  { value: 'pendiente_entrega', label: 'Pendiente entrega del traje', color: 'indigo' },
  { value: 'listo_cobro', label: 'Listo para cobro', color: 'yellow' },
  { value: 'cuenta_cobro_generada', label: 'Cuenta de cobro generada', color: 'teal' },
  { value: 'pagado', label: 'Pagado', color: 'emerald' },
  { value: 'rechazado', label: 'Rechazado', color: 'red' }
];

// Estados de comisión para admin
const ESTADOS_COMISION = [
  { value: 'pendiente', label: 'Pendiente', color: 'zinc' },
  { value: 'calculada', label: 'Calculada', color: 'blue' },
  { value: 'aprobada', label: 'Aprobada', color: 'green' },
  { value: 'cuenta_cobro_generada', label: 'Cuenta de cobro generada', color: 'teal' },
  { value: 'pagada', label: 'Pagada', color: 'emerald' },
  { value: 'rechazada', label: 'Rechazada', color: 'red' }
];

const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

// Componente para buscar y vincular cotizaciones
const CotizacionBuscador = ({ formData, setFormData, aliado_id, referido_id, isEditing }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [vinculando, setVinculando] = useState(false);

  const buscarCotizaciones = async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/cotizaciones/buscar?search=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data.cotizaciones || []);
      setShowResults(true);
    } catch (error) {
      console.debug('Error buscando cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      buscarCotizaciones(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const vincularCotizacion = async (cotizacion) => {
    if (!isEditing || !referido_id) {
      // Si es nuevo, solo actualizar el form localmente
      setFormData(prev => ({
        ...prev,
        id_cotizacion: cotizacion.id,
        numero_cotizacion: cotizacion.numero,
        valor_cotizacion: cotizacion.total || 0
      }));
      setShowResults(false);
      setSearch('');
      return;
    }
    
    setVinculando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/aliados/referidos/${referido_id}/vincular-cotizacion?cotizacion_id=${cotizacion.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const updatedRef = response.data.referido;
      setFormData(prev => ({
        ...prev,
        id_cotizacion: updatedRef.id_cotizacion,
        numero_cotizacion: updatedRef.numero_cotizacion,
        valor_cotizacion: updatedRef.valor_cotizacion,
        estado: updatedRef.estado,
        fue_venta: updatedRef.fue_venta
      }));
      
      toast.success(`Cotización vinculada. Comisión: ${formatPrice(response.data.comision_calculada)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al vincular');
    } finally {
      setVinculando(false);
      setShowResults(false);
      setSearch('');
    }
  };

  const desvincularCotizacion = async () => {
    if (!isEditing || !referido_id) {
      setFormData(prev => ({
        ...prev,
        id_cotizacion: '',
        numero_cotizacion: '',
        valor_cotizacion: ''
      }));
      return;
    }
    
    setVinculando(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/aliados/referidos/${referido_id}/desvincular-cotizacion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setFormData(prev => ({
        ...prev,
        id_cotizacion: '',
        numero_cotizacion: '',
        valor_cotizacion: '',
        estado: 'registrado',
        fue_venta: null
      }));
      
      toast.success('Cotización desvinculada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desvincular');
    } finally {
      setVinculando(false);
    }
  };

  // Si ya hay una cotización vinculada
  if (formData.id_cotizacion) {
    return (
      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link2 className="w-5 h-5 text-green-500" />
            <div>
              <p className="font-medium text-green-400">Cotización Vinculada</p>
              <p className="text-sm text-zinc-400">
                #{formData.numero_cotizacion} - {formatPrice(formData.valor_cotizacion)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={desvincularCotizacion}
            disabled={vinculando}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            title="Desvincular cotización"
          >
            {vinculando ? (
              <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <X className="w-4 h-4 text-red-400" />
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <label className="block text-sm font-medium mb-2">
        <Link2 className="w-4 h-4 inline mr-1" />
        Vincular Cotización Existente
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white placeholder-zinc-500"
          placeholder="Buscar por nombre, teléfono o número de cotización..."
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
      
      {/* Resultados */}
      {showResults && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-h-64 overflow-y-auto">
          {results.map((cot) => (
            <button
              key={cot.id}
              type="button"
              onClick={() => vincularCotizacion(cot)}
              disabled={vinculando}
              className="w-full p-3 text-left hover:bg-zinc-800 transition-colors border-b border-zinc-800 last:border-0"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{cot.cliente_nombre}</p>
                  <p className="text-sm text-zinc-400">
                    #{cot.numero} - {cot.cliente_telefono}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[#D4AF37] font-medium">{formatPrice(cot.total)}</p>
                  <p className="text-xs text-zinc-500">{cot.estado || 'pendiente'}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {showResults && results.length === 0 && search.length >= 2 && !loading && (
        <div className="absolute z-50 left-0 right-0 mt-1 p-4 bg-zinc-900 border border-zinc-700 rounded-lg text-center text-zinc-400">
          No se encontraron cotizaciones
        </div>
      )}
      
      {/* Overlay para cerrar */}
      {showResults && (
        <div className="fixed inset-0 z-40" onClick={() => setShowResults(false)} />
      )}
    </div>
  );
};

const ReferidoForm = () => {
  const navigate = useNavigate();
  const { aliado_id, id } = useParams();
  const { user } = useAuth();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aliado, setAliado] = useState(null);
  const [weddings, setWeddings] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  
  const [formData, setFormData] = useState({
    id_evento: '',
    tipo_referido: 'novio',
    nombre: '',
    telefono: '',
    cedula: '',
    correo: '',
    fecha_evento: '',
    fecha_entrega_traje: '',
    observaciones: '',
    estado: 'registrado',
    estado_comision: 'pendiente',  // Estado de la comisión
    id_cotizacion: '',
    numero_cotizacion: '',
    valor_cotizacion: '',
    valor_comision_manual: '',  // Comisión manual para Admin/Gerencia
    fue_venta: null
  });

  // Calcular comisión estimada
  const tipoSeleccionado = TIPOS_REFERIDO.find(t => t.value === formData.tipo_referido);
  const comisionEstimada = formData.valor_cotizacion && tipoSeleccionado 
    ? (parseFloat(formData.valor_cotizacion) * tipoSeleccionado.comision / 100)
    : 0;

  // Estado para generar cuenta de cobro
  const [generandoCuenta, setGenerandoCuenta] = useState(false);

  // Función para generar cuenta de cobro
  const generarCuentaCobro = async () => {
    if (!aliado_id || !id) return;
    
    setGenerandoCuenta(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/aliados/${aliado_id}/cuenta-cobro/generar`,
        { referidos_ids: [id] },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Descargar el PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cuenta_cobro_${aliado?.nombre?.replace(/\s/g, '_') || 'aliado'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Cuenta de cobro generada y descargada');
      
      // Recargar datos del referido para actualizar estado
      fetchReferido();
    } catch (error) {
      console.error('Error generando cuenta:', error);
      const errorMsg = error.response?.data?.detail || 'Error al generar cuenta de cobro';
      toast.error(errorMsg);
    } finally {
      setGenerandoCuenta(false);
    }
  };

  const fetchAliado = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/${aliado_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAliado(response.data);
    } catch (error) {
      console.debug('Error fetching aliado');
    }
  }, [aliado_id]);

  const fetchWeddings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/${aliado_id}/weddings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeddings(response.data.weddings || []);
    } catch (error) {
      console.debug('Error fetching weddings');
    }
  }, [aliado_id]);

  const fetchReferido = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Obtener todos los referidos y filtrar
      const response = await axios.get(`${API_URL}/api/aliados/${aliado_id}/referidos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const referido = response.data.referidos?.find(r => r.id === id);
      if (referido) {
        setFormData({
          id_evento: referido.id_evento || '',
          tipo_referido: referido.tipo_referido || 'novio',
          nombre: referido.nombre || '',
          telefono: referido.telefono || '',
          cedula: referido.cedula || '',
          correo: referido.correo || '',
          fecha_evento: referido.fecha_evento ? referido.fecha_evento.split('T')[0] : '',
          fecha_entrega_traje: referido.fecha_entrega_traje ? referido.fecha_entrega_traje.split('T')[0] : '',
          observaciones: referido.observaciones || '',
          estado: referido.estado || 'registrado',
          estado_comision: referido.estado_comision || 'pendiente',
          id_cotizacion: referido.id_cotizacion || '',
          numero_cotizacion: referido.numero_cotizacion || '',
          valor_cotizacion: referido.valor_cotizacion || '',
          valor_comision_manual: referido.valor_comision || '',  // Cargar comisión existente
          fue_venta: referido.fue_venta
        });
      } else {
        toast.error('Referido no encontrado');
        navigate(`/aliados/${aliado_id}/referidos`);
      }
    } catch (error) {
      toast.error('Error al cargar el referido');
    } finally {
      setLoading(false);
    }
  }, [aliado_id, id, navigate]);

  useEffect(() => {
    fetchAliado();
    fetchWeddings();
    if (isEditing) {
      fetchReferido();
    }
  }, [fetchAliado, fetchWeddings, fetchReferido, isEditing]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    if (name === 'valor_cotizacion') {
      // Limpiar caracteres no numéricos
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else if (name === 'fue_venta') {
      setFormData(prev => ({ ...prev, [name]: value === '' ? null : value === 'true' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!formData.telefono.trim()) {
      toast.error('El teléfono es obligatorio');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        valor_cotizacion: formData.valor_cotizacion ? parseFloat(formData.valor_cotizacion) : null,
        // Si hay comisión manual y no hay cotización, enviarla como valor_comision
        valor_comision: formData.valor_comision_manual && !formData.valor_cotizacion 
          ? parseFloat(formData.valor_comision_manual) 
          : undefined
      };
      
      // Limpiar campos no necesarios
      delete payload.valor_comision_manual;
      
      if (isEditing) {
        await axios.put(`${API_URL}/api/aliados/referidos/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Referido actualizado exitosamente');
      } else {
        await axios.post(`${API_URL}/api/aliados/${aliado_id}/referidos`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Referido creado exitosamente');
      }
      
      navigate(`/aliados/${aliado_id}/referidos`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el referido');
    } finally {
      setSaving(false);
    }
  };

  // Validar si se puede generar comisión
  // Admin y Gerencia pueden generar comisión sin cotización vinculada
  const isPrivileged = user?.role === 'admin' || user?.role === 'gerencia';
  
  const canGenerateCommission = () => {
    const issues = [];
    if (!formData.fecha_entrega_traje) issues.push('Falta fecha de entrega del traje');
    
    // Solo validar cotización si NO es admin/gerencia
    if (!isPrivileged) {
      if (formData.fue_venta !== true) issues.push('La cotización no está marcada como venta realizada');
      if (!formData.valor_cotizacion || parseFloat(formData.valor_cotizacion) <= 0) issues.push('No hay valor de cotización');
    } else {
      // Para admin/gerencia: debe haber cotización O comisión manual
      const tieneComision = (formData.valor_cotizacion && parseFloat(formData.valor_cotizacion) > 0) || 
                           (formData.valor_comision_manual && parseFloat(formData.valor_comision_manual) > 0);
      if (!tieneComision) issues.push('Ingrese valor de cotización o comisión manual');
    }
    
    if (!aliado?.datos_cobro?.numero_cuenta) issues.push('El aliado no tiene datos bancarios completos');
    return issues;
  };

  const commissionIssues = canGenerateCommission();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="bg-black border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/aliados/${aliado_id}/referidos`)}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Referidos</span>
              </button>
              <div className="h-6 w-px bg-zinc-800" />
              <div>
                <h1 className="font-display text-lg">{isEditing ? 'Editar Referido' : 'Nuevo Referido'}</h1>
                {aliado && <p className="text-xs text-zinc-400">{aliado.nombre}</p>}
              </div>
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg hover:bg-[#C49F27] disabled:opacity-50 transition-colors font-medium"
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">{saving ? 'Guardando...' : 'Guardar'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Tipo de Referido */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="font-display text-lg mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#D4AF37]" />
              Tipo de Referido
            </h2>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {TIPOS_REFERIDO.map((tipo) => (
                <button
                  key={tipo.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, tipo_referido: tipo.value }))}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    formData.tipo_referido === tipo.value
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {tipo.value === 'mascota' ? (
                      <Heart className={`w-4 h-4 ${formData.tipo_referido === tipo.value ? 'text-[#D4AF37]' : 'text-zinc-400'}`} />
                    ) : (
                      <User className={`w-4 h-4 ${formData.tipo_referido === tipo.value ? 'text-[#D4AF37]' : 'text-zinc-400'}`} />
                    )}
                    <span className="font-medium">{tipo.label}</span>
                  </div>
                  <p className="text-xs text-zinc-400">{tipo.descripcion}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Datos del Referido */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="font-display text-lg mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#D4AF37]" />
              Datos del Referido
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Nombre Completo *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  placeholder="Nombre del referido"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Teléfono *
                </label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  placeholder="3001234567"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Cédula</label>
                <input
                  type="text"
                  name="cedula"
                  value={formData.cedula}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  placeholder="Número de cédula"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Correo Electrónico
                </label>
                <input
                  type="email"
                  name="correo"
                  value={formData.correo}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  placeholder="correo@ejemplo.com"
                />
              </div>
            </div>
          </div>

          {/* Evento y Fechas */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="font-display text-lg mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              Evento y Fechas
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {weddings.length > 0 && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Wedding / Evento Asociado</label>
                  <select
                    name="id_evento"
                    value={formData.id_evento}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  >
                    <option value="">Sin evento asociado</option>
                    {weddings.map(w => (
                      <option key={w.id} value={w.id}>{w.nombre_evento} - {w.nombre_novios}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-2">Fecha del Evento</label>
                <input
                  type="date"
                  name="fecha_evento"
                  value={formData.fecha_evento}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  Fecha de Entrega del Traje
                  <span className="text-amber-500 ml-1">*</span>
                </label>
                <input
                  type="date"
                  name="fecha_entrega_traje"
                  value={formData.fecha_entrega_traje}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                />
                <p className="text-xs text-zinc-500 mt-1">Requerida para generar comisión</p>
              </div>
            </div>
          </div>

          {/* Cotización y Estado (solo para admin/gerencia) */}
          {user?.role !== 'aliado' && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="font-display text-lg mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                Cotización y Estado
              </h2>
              
              {/* Buscador de cotizaciones */}
              <CotizacionBuscador 
                formData={formData}
                setFormData={setFormData}
                aliado_id={aliado_id}
                referido_id={id}
                isEditing={isEditing}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Valor de Cotización</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <input
                      type="text"
                      name="valor_cotizacion"
                      value={formData.valor_cotizacion ? parseInt(formData.valor_cotizacion).toLocaleString('es-CO') : ''}
                      onChange={handleChange}
                      className="w-full pl-8 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                      placeholder="0"
                      disabled={!!formData.id_cotizacion}
                    />
                  </div>
                  {formData.id_cotizacion && (
                    <p className="text-xs text-zinc-500 mt-1">Valor importado de la cotización vinculada</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Estado del Referido</label>
                  <select
                    name="estado"
                    value={formData.estado}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  >
                    {ESTADOS_REFERIDO.map(estado => (
                      <option key={estado.value} value={estado.value}>{estado.label}</option>
                    ))}
                  </select>
                </div>

                {/* Estado de Comisión - Solo para Admin */}
                {isEditing && (user?.role === 'admin' || user?.role === 'gerencia') && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Estado de Comisión
                      <span className="ml-2 text-xs text-amber-400">(Admin)</span>
                    </label>
                    <select
                      name="estado_comision"
                      value={formData.estado_comision}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-zinc-800 border border-amber-700/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-white"
                    >
                      {ESTADOS_COMISION.map(estado => (
                        <option key={estado.value} value={estado.value}>{estado.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-amber-400/70 mt-1">
                      Cambiar a &quot;Calculada&quot; o &quot;Aprobada&quot; para permitir generar cuenta de cobro
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-2">¿Fue Venta?</label>
                  <select
                    name="fue_venta"
                    value={formData.fue_venta === null ? '' : formData.fue_venta.toString()}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  >
                    <option value="">Pendiente</option>
                    <option value="true">Sí - Venta realizada</option>
                    <option value="false">No - No fue venta</option>
                  </select>
                </div>
                
                {/* Comisión estimada con desglose IVA */}
                {formData.valor_cotizacion > 0 && (
                  <div className="md:col-span-2 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-zinc-400">Valor total con IVA</p>
                        <p className="text-lg font-display">{formatPrice(formData.valor_cotizacion)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-400">Base sin IVA (÷1.19)</p>
                        <p className="text-lg font-display">
                          {formatPrice(formData.valor_base_sin_iva || Math.round(formData.valor_cotizacion / 1.19))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-400">IVA incluido (19%)</p>
                        <p className="text-lg font-display text-zinc-400">
                          {formatPrice(formData.iva_incluido_valor || Math.round(formData.valor_cotizacion - (formData.valor_cotizacion / 1.19)))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-400">Comisión ({tipoSeleccionado?.comision || 0}% s/base)</p>
                        <p className="text-2xl font-display text-[#D4AF37]">
                          {formatPrice(formData.valor_comision || Math.round((formData.valor_cotizacion / 1.19) * ((tipoSeleccionado?.comision || 0) / 100)))}
                        </p>
                      </div>
                    </div>
                    {formData.fue_venta === true && (
                      <div className="mt-3 pt-3 border-t border-[#D4AF37]/30 flex items-center justify-between">
                        <span className="text-sm text-green-400 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Venta confirmada - Comisión calculada sobre base sin IVA
                        </span>
                      </div>
                    )}
                    <p className="text-xs text-zinc-500 mt-2">
                      La comisión NUNCA se calcula sobre el valor con IVA. Siempre sobre la base (total ÷ 1.19)
                    </p>
                  </div>
                )}

                {/* Comisión Manual - Solo para Admin/Gerencia sin cotización */}
                {isPrivileged && !formData.valor_cotizacion && (
                  <div className="md:col-span-2 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <h4 className="text-sm font-medium text-purple-400 mb-3">
                      Comisión Manual (Admin/Gerencia)
                    </h4>
                    <p className="text-xs text-purple-300/70 mb-3">
                      Sin cotización vinculada, puede ingresar el valor de la comisión directamente.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-purple-300/70 mb-1">Valor de Comisión</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400/50 text-sm">$</span>
                          <input
                            type="number"
                            name="valor_comision_manual"
                            value={formData.valor_comision_manual || ''}
                            onChange={handleChange}
                            placeholder="Ej: 500000"
                            className="w-full px-4 py-2 pl-8 bg-purple-500/10 border border-purple-500/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-white"
                          />
                        </div>
                      </div>
                      <div className="flex items-end">
                        <p className="text-lg font-display text-purple-400">
                          {formData.valor_comision_manual ? formatPrice(parseFloat(formData.valor_comision_manual)) : '$0'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Validación de Comisión y Botón Generar Cuenta */}
          {isEditing && (
            <div className="space-y-4">
              {/* Mostrar problemas si los hay */}
              {commissionIssues.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-200 font-medium">No se puede generar cuenta de cobro</p>
                      <ul className="mt-2 space-y-1 text-sm text-amber-200/70">
                        {commissionIssues.map((issue) => (
                          <li key={`issue-${issue.substring(0, 30)}`}>• {issue}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Botón Generar Cuenta de Cobro - Visible cuando se puede generar */}
              {commissionIssues.length === 0 && (formData.estado_comision === 'calculada' || formData.estado_comision === 'aprobada') && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="text-green-200 font-medium">Listo para generar cuenta de cobro</p>
                        <p className="text-sm text-green-200/70">
                          Comisión: {formatPrice(formData.valor_comision_manual || (formData.valor_cotizacion ? Math.round((formData.valor_cotizacion / 1.19) * ((tipoSeleccionado?.comision || 0) / 100)) : 0))}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={generarCuentaCobro}
                      disabled={generandoCuenta}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {generandoCuenta ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generando...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          Generar Cuenta de Cobro
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Estado de cuenta ya generada */}
              {formData.estado_comision === 'cuenta_cobro_generada' && (
                <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-teal-400" />
                      <div>
                        <p className="text-teal-200 font-medium">Cuenta de cobro ya generada</p>
                        <p className="text-sm text-teal-200/70">
                          Para regenerar, cambie el &quot;Estado de Comisión&quot; a &quot;Calculada&quot; o &quot;Aprobada&quot;
                        </p>
                      </div>
                    </div>
                    {isPrivileged && (
                      <button
                        type="button"
                        onClick={generarCuentaCobro}
                        disabled={generandoCuenta}
                        className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                      >
                        {generandoCuenta ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Regenerando...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Regenerar PDF
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Observaciones */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="font-display text-lg mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              Observaciones
            </h2>
            
            <textarea
              name="observaciones"
              value={formData.observaciones}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white resize-none"
              placeholder="Notas adicionales sobre el referido..."
            />
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(`/aliados/${aliado_id}/referidos`)}
              className="px-6 py-2.5 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-2.5 rounded-lg hover:bg-[#C49F27] disabled:opacity-50 transition-colors font-medium"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : (isEditing ? 'Actualizar Referido' : 'Crear Referido')}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ReferidoForm;
